import { useState, type ReactNode } from 'react';
import { ApiError } from '../../../lib/api';
import {
  fetchEfacturaFile,
  type EfacturaKind,
  type ParsedDocument,
} from '../../../lib/efacturaApi';

// Building blocks shared by FINANCIAR › SPV e-Factura and the Facturi page,
// so an invoice looks and behaves the same wherever it is listed: the kind
// badges, the money/date formatting, the payment-mark switch, the expanded
// detail frame with its file buttons and the parsed-document view.

export const KIND_LABELS: Record<EfacturaKind, string> = {
  PRIMITA: 'Primită',
  TRIMISA: 'Trimisă',
  ERORI: 'Respinsă (erori)',
  MESAJ_CUMPARATOR: 'Mesaj cumpărător',
  ALTUL: 'Altul',
};

export const KIND_STYLES: Record<EfacturaKind, string> = {
  PRIMITA: 'bg-blue-100 text-blue-700',
  TRIMISA: 'bg-green-100 text-green-700',
  ERORI: 'bg-red-100 text-red-700',
  MESAJ_CUMPARATOR: 'bg-purple-100 text-purple-700',
  ALTUL: 'bg-gray-100 text-gray-600',
};

export const FIELD_CLASS =
  'rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none';

/** Display only: the string amount is formatted, never computed with. */
export function money(amount: string | number | null, currency: string | null): string {
  if (amount === null) return '—';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency ?? ''}`.trim();
  return `${n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim();
}

export function date(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

export function dateTime(value: string): string {
  return new Date(value).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' });
}

/** Kind badge (+ "storno" tag) as shown in the first column of both tables. */
export function KindBadge({ kind, storno }: { kind: EfacturaKind; storno?: boolean }) {
  return (
    <>
      <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${KIND_STYLES[kind]}`}>
        {KIND_LABELS[kind]}
      </span>
      {storno && (
        // Amber on purpose: the neutral ramp is inverted in dark mode, so a
        // gray-100 pill would melt into the row surface there.
        <span className="ml-1 inline-block rounded border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
          storno
        </span>
      )}
    </>
  );
}

/**
 * The hand-kept payment mark of one row: "Achitată" on a received invoice,
 * "Încasată" on a sent one. A compact switch — the table cell has no room
 * for the labelled `ui/Switch`. `label` null = the row carries no such
 * status (errors, buyer messages, "other").
 */
export function SettledSwitch({
  label,
  settledAt,
  canSettle,
  settling,
  onSettle,
}: {
  label: string | null;
  settledAt: string | null;
  canSettle: boolean;
  settling: boolean;
  onSettle: (settled: boolean) => void;
}) {
  if (!label) return <span className="text-xs text-gray-400">—</span>;
  const on = settledAt !== null;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={
        on
          ? `${label} · ${new Date(settledAt as string).toLocaleDateString('ro-RO')}`
          : `Marchează ${label.toLowerCase()}`
      }
      disabled={!canSettle || settling}
      onClick={(e) => {
        e.stopPropagation();
        onSettle(!on);
      }}
      className={`inline-flex items-center gap-2 rounded-full py-0.5 pl-0.5 pr-2 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        on ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      <span
        aria-hidden
        className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
          on ? 'bg-emerald-600' : 'bg-gray-400'
        }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </span>
      {label}
    </button>
  );
}

export type EfacturaFileKind = 'pdf' | 'xml' | 'archive';

/** Opens the PDF / XML / signed archive of an SPV message in a new tab or as a download. */
export function useEfacturaFiles(messageId: string | null, anafId: string | null) {
  const [busy, setBusy] = useState<EfacturaFileKind | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  async function open(kind: EfacturaFileKind) {
    if (!messageId) return;
    setBusy(kind);
    setFileError(null);
    try {
      const { url } = await fetchEfacturaFile(messageId, kind);
      if (kind === 'archive') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `spv-${anafId ?? messageId}.zip`;
        a.click();
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setFileError(err instanceof ApiError ? err.message : 'Descărcarea a eșuat.');
    } finally {
      setBusy(null);
    }
  }

  return { busy, fileError, open };
}

const PRIMARY_BTN =
  'rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';
const SECONDARY_BTN =
  'rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50';

/** The PDF / XML / signed-archive buttons of an SPV message. */
export function EfacturaFileButtons({
  hasInvoice,
  busy,
  onOpen,
}: {
  hasInvoice: boolean;
  busy: EfacturaFileKind | null;
  onOpen: (kind: EfacturaFileKind) => void;
}) {
  return (
    <>
      {hasInvoice && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => onOpen('pdf')}
          className={PRIMARY_BTN}
        >
          {busy === 'pdf' ? 'Se generează…' : 'PDF'}
        </button>
      )}
      <button
        type="button"
        disabled={busy !== null}
        onClick={() => onOpen('xml')}
        className={SECONDARY_BTN}
      >
        XML
      </button>
      <button
        type="button"
        disabled={busy !== null}
        onClick={() => onOpen('archive')}
        className={SECONDARY_BTN}
        title="Arhiva originală: factura + semnătura Ministerului de Finanțe"
      >
        Arhivă semnată (ZIP)
      </button>
    </>
  );
}

/**
 * The expanded row under a table line: a header line on the left, the action
 * buttons on the right (ending with "Închide"), then the body.
 */
export function DetailFrame({
  colSpan,
  title,
  actions,
  error,
  notice,
  onClose,
  children,
}: {
  colSpan: number;
  title: ReactNode;
  actions?: ReactNode;
  error?: string | null;
  notice?: string | null;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="bg-gray-50 px-3 py-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-600">{title}</div>
          <div className="flex flex-wrap gap-2">
            {actions}
            <button
              type="button"
              onClick={onClose}
              className="rounded px-2 py-1 text-sm text-gray-500 hover:text-gray-800"
            >
              Închide
            </button>
          </div>
        </div>
        {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
        {notice && (
          <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {notice}
          </p>
        )}
        {children}
      </td>
    </tr>
  );
}

/** Supplier / customer card used at the top of a document view. */
export function PartyCard({
  label,
  name,
  ids,
  address,
}: {
  label: string;
  name: string | null;
  ids?: string | null;
  address?: string | null;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="font-medium text-gray-900">{name || '—'}</div>
      {ids && <div className="text-gray-600">{ids}</div>}
      {address && <div className="text-gray-500">{address}</div>}
    </div>
  );
}

export interface DocLine {
  id: string;
  description: string;
  quantity: string | number;
  unit: string;
  unitPrice: string | number;
  vat: string;
  lineTotal: string | number;
}

/** The line-items table of a document view. */
export function LinesTable({ lines, currency }: { lines: DocLine[]; currency: string | null }) {
  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-1.5 text-left font-medium text-gray-600">#</th>
            <th className="px-3 py-1.5 text-left font-medium text-gray-600">Descriere</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">Cant.</th>
            <th className="px-3 py-1.5 text-left font-medium text-gray-600">UM</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">Preț unitar</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">TVA %</th>
            <th className="px-3 py-1.5 text-right font-medium text-gray-600">Valoare</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {lines.map((l) => (
            <tr key={l.id}>
              <td className="px-3 py-1.5 text-gray-500">{l.id}</td>
              <td className="px-3 py-1.5 text-gray-900">{l.description}</td>
              <td className="px-3 py-1.5 text-right text-gray-700">{l.quantity}</td>
              <td className="px-3 py-1.5 text-gray-700">{l.unit}</td>
              <td className="px-3 py-1.5 text-right text-gray-700">
                {money(l.unitPrice, currency)}
              </td>
              <td className="px-3 py-1.5 text-right text-gray-700">{l.vat || '—'}</td>
              <td className="px-3 py-1.5 text-right text-gray-900">
                {money(l.lineTotal, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The parsed SPV document: parties, references, lines, totals, notes. */
export function InvoiceDetails({ doc }: { doc: ParsedDocument }) {
  if (doc.tip === 'erori') {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <strong>Factura a fost respinsă de ANAF.</strong>
        <ul className="mt-1 list-disc pl-5">
          {doc.errors.length === 0 && <li>Fără detalii de eroare în arhivă.</li>}
          {doc.errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </div>
    );
  }
  const cur = doc.currency;
  return (
    <div className="space-y-3 text-sm">
      <div className="grid gap-3 sm:grid-cols-2">
        <PartyCard
          label="Furnizor"
          name={doc.supplier.name}
          ids={[doc.supplier.vatId, doc.supplier.registrationNumber].filter(Boolean).join(' · ')}
          address={doc.supplier.address}
        />
        <PartyCard
          label="Cumpărător"
          name={doc.customer.name}
          ids={[doc.customer.vatId, doc.customer.registrationNumber].filter(Boolean).join(' · ')}
          address={doc.customer.address}
        />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-700">
        <span>
          Tip: <strong>{doc.tip === 'storno' ? 'Notă de credit (storno)' : 'Factură'}</strong> (
          {doc.typeCode || '—'})
        </span>
        <span>
          Scadență: <strong>{doc.dueDate || '—'}</strong>
        </span>
        {doc.billingReference && (
          <span>
            Stornează: <strong>{doc.billingReference}</strong>
          </span>
        )}
        {doc.paymentReference && (
          <span>
            Referință plată: <strong>{doc.paymentReference}</strong>
          </span>
        )}
        {doc.payment.iban && (
          <span>
            IBAN: <strong>{doc.payment.iban}</strong>
          </span>
        )}
        {doc.orderReference && (
          <span>
            Comandă: <strong>{doc.orderReference}</strong>
          </span>
        )}
      </div>
      <LinesTable
        currency={cur}
        lines={doc.lines.map((l) => ({
          id: l.id,
          description: l.description,
          quantity: l.quantity,
          unit: l.unit,
          unitPrice: l.unitPrice,
          vat: l.vatPercent || l.vatCategory,
          lineTotal: l.lineTotal,
        }))}
      />
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-gray-700">
        <span>
          Fără TVA: <strong>{money(doc.totals.withoutVat, cur)}</strong>
        </span>
        <span>
          TVA: <strong>{money(doc.totals.vat, cur)}</strong>
          {doc.totals.vatRon && cur !== 'RON' && ` (${money(doc.totals.vatRon, 'RON')})`}
        </span>
        <span>
          Cu TVA: <strong>{money(doc.totals.withVat, cur)}</strong>
        </span>
        {doc.totals.prepaid && (
          <span>
            Plătit în avans: <strong>{money(doc.totals.prepaid, cur)}</strong>
          </span>
        )}
        <span>
          De plată: <strong>{money(doc.totals.payable, cur)}</strong>
        </span>
      </div>
      {doc.notes.length > 0 && (
        <div className="text-gray-600">
          {doc.notes.map((n, i) => (
            <p key={i}>{n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
